import { useState, useEffect } from "react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useClubs, useUpdateDisabledClubs } from "@/modules/leagues/hooks/useLeagueAdmin";
import { SettingsSubHeader } from "@/modules/leagues/components/SettingsSubHeader";
import { Switch } from "@/components/atoms/Switch";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { useToast } from "@/hooks/use-toast";

export function LeagueSettingsClubsPage() {
  const { league } = useLeagueContext();
  const { toast } = useToast();
  const { data: clubs, isLoading } = useClubs(league.competitionId);
  const updateDisabled = useUpdateDisabledClubs(league.id);
  const [disabledIds, setDisabledIds] = useState<string[]>(league.disabledClubIds);

  useEffect(() => {
    setDisabledIds(league.disabledClubIds);
  }, [league.disabledClubIds]);

  function toggle(clubId: string) {
    setDisabledIds((prev) => (prev.includes(clubId) ? prev.filter((id) => id !== clubId) : [...prev, clubId]));
  }

  function handleSave() {
    updateDisabled.mutate(disabledIds, {
      onSuccess: () =>
        toast({
          title: "Clubes atualizados",
          description: "Partidas dos clubes desativados não geram pontuação nesta liga.",
        }),
      onError: (err) => toast({ title: "Não foi possível salvar", description: err.message, variant: "destructive" }),
    });
  }

  return (
    <div className="space-y-4">
      <SettingsSubHeader leagueId={league.id} title="Selecionar Clubes" />
      <p className="text-xs text-muted-foreground">
        Desative clubes que não participam desta liga. Partidas envolvendo clubes desativados não geram pontuação
        nem aparecem para palpite.
      </p>

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      <div className="space-y-2">
        {clubs?.map((club) => {
          const enabled = !disabledIds.includes(club.id);
          return (
            <div key={club.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3">
              <div className="flex items-center gap-3">
                {club.crestUrl ? (
                  <img src={club.crestUrl} alt={club.name} className="h-9 w-9 object-contain" loading="lazy" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-display text-[11px] font-bold text-primary-700 dark:bg-primary-900 dark:text-primary-200">
                    {club.shortName}
                  </div>
                )}
                <span className="text-sm font-medium">{club.name}</span>
              </div>
              <Switch checked={enabled} onCheckedChange={() => toggle(club.id)} />
            </div>
          );
        })}
      </div>

      <Button className="w-full" isLoading={updateDisabled.isPending} onClick={handleSave}>
        Salvar seleção de clubes
      </Button>
    </div>
  );
}
